import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import queryString from "query-string";
import {
    SovosBox,
    SovosSnackbar,
} from "mosaic-react";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import { parseFilter, parseSort } from "colbi_web_ui/lib/utils/queryUtils";
import ModalHeaderCloseCustom from "../../../../components/ModalHeaderCloseCustom/ModalHeaderCloseCustom";
import { pushModal } from "../../../actions";
import { formatConditionSummary } from "./featureFlags";
import RulesTable from "./RulesTable";
import {
    clearRulesGridQuery,
    filterRules,
    resetRulesPage,
    sortRules,
} from "./rulesTableState";

const RulesList = ({
    canMutate = true,
    nonOwnerTooltip: providedNonOwnerTooltip,
    onList,
    onToggleState,
    profileId,
    projectId,
}) => {
    const { history, i18n, location } = useGlobalValue();
    const dispatch = useDispatch();
    const initialRouteRef = useRef({
        pathname: location?.pathname,
        search: location?.search || "",
    });
    const queryInitializedRef = useRef(false);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState(null);
    const [queryReady, setQueryReady] = useState(false);
    const [rules, setRules] = useState([]);
    const [statusFilter, setStatusFilter] = useState("All");
    const nonOwnerTooltip =
        providedNonOwnerTooltip || i18n`Only the project owner can manage rules.`;

    const refresh = useCallback(async (showLoading = true) => {
        if (!onList || !profileId || !projectId) return;
        if (showLoading) setLoading(true);
        try {
            const result = await onList(profileId, projectId);
            if (!Array.isArray(result)) {
                setNotification({
                    message: i18n`Rules could not be loaded. Existing results were preserved.`,
                    type: "error",
                });
                return;
            }
            setRules(result);
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [i18n, onList, profileId, projectId]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (!rules.some((rule) => rule.state === "Processing")) return undefined;
        const handle = setInterval(() => refresh(false), 6000);
        return () => clearInterval(handle);
    }, [refresh, rules]);

    useEffect(
        () => () => {
            const initialRoute = initialRouteRef.current;
            if (
                history?.replace
                && history.location?.pathname === initialRoute.pathname
            ) {
                history.replace({ search: initialRoute.search });
            }
        },
        [history],
    );

    useEffect(() => {
        if (queryInitializedRef.current) return;
        const currentSearch = (location?.search || "").replace(/^\?/, "");
        const nextSearch = clearRulesGridQuery(currentSearch);
        if (history?.replace && nextSearch !== currentSearch) {
            history.replace({ search: nextSearch });
            queryInitializedRef.current = true;
            setQueryReady(true);
            return;
        }
        queryInitializedRef.current = true;
        setQueryReady(true);
    }, [history, location?.search]);

    const statusFilteredRules = useMemo(
        () => statusFilter === "All"
            ? rules
            : rules.filter((rule) => rule.state === statusFilter),
        [rules, statusFilter],
    );
    const queryParams = useMemo(
        () => queryString.parse(location?.search || ""),
        [location?.search],
    );
    const gridFilter = queryParams.filter || "";
    const gridSort = queryParams.sort || "";
    const parsedFilters = useMemo(() => parseFilter(gridFilter) || [], [gridFilter]);
    const parsedSort = useMemo(() => parseSort(gridSort) || [], [gridSort]);
    const formatDescription = useCallback(
        (rule) => formatConditionSummary(rule, i18n),
        [i18n],
    );
    const processedRules = useMemo(
        () => sortRules(
            filterRules(statusFilteredRules, parsedFilters, formatDescription),
            parsedSort,
            formatDescription,
        ),
        [formatDescription, parsedFilters, parsedSort, statusFilteredRules],
    );
    const pageSize = Number(queryParams.pageSize) || 20;
    const page = Number(queryParams.page) || 0;
    const pagedRules = useMemo(
        () => processedRules.slice(page * pageSize, (page + 1) * pageSize),
        [page, pageSize, processedRules],
    );
    const handleStatusFilterChange = useCallback(
        (value) => {
            setStatusFilter(value);
            if (!history?.replace) return;
            history.replace({ search: resetRulesPage(location?.search) });
        },
        [history, location?.search],
    );

    const handleToggleState = useCallback(async (rule) => {
        if (!canMutate || !onToggleState || rule.state === "Processing") return;
        const nextState = rule.state === "Active" ? "Inactive" : "Active";
        const updated = await onToggleState(rule.id, nextState);
        if (!updated) {
            setNotification({
                message: i18n`The rule status could not be updated.`,
                type: "error",
            });
            return;
        }
        await refresh(false);
    }, [canMutate, i18n, onToggleState, refresh]);
    const handleOpenRule = useCallback(
        (rule) => {
            dispatch(
                pushModal({
                    customHeaderButton: ModalHeaderCloseCustom,
                    data: {
                        description: formatConditionSummary(rule, i18n),
                        rule,
                    },
                    title: rule.name,
                    view: "RuleDetails",
                }),
            ).catch(() => null);
        },
        [dispatch, i18n],
    );

    return (
        <SovosBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {queryReady && (
                <RulesTable
                    canMutate={canMutate}
                    filter={gridFilter}
                    loading={loading}
                    nonOwnerTooltip={nonOwnerTooltip}
                    onOpenRule={handleOpenRule}
                    onStatusFilterChange={handleStatusFilterChange}
                    onToggleState={handleToggleState}
                    page={page}
                    pageSize={pageSize}
                    rules={pagedRules}
                    sort={gridSort}
                    statusFilter={statusFilter}
                    total={processedRules.length}
                />
            )}

            <SovosSnackbar
                anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
                autoHideDuration={5000}
                message={notification?.message}
                onClose={() => setNotification(null)}
                open={!!notification}
                type={notification?.type}
            />
        </SovosBox>
    );
};

export default RulesList;
